import { LuxTypography } from '@gemini/fe-ds-core';
import useNoopClickableGuard from '@hooks/useNoopClickableGuard';
import { ReactNode, isValidElement } from 'react';
import {
  CellWrapper,
  HeaderCellWrapper,
  RowWrapper,
  TableBodyWrapper,
  TableHeaderWrapper,
  TableWrapper,
} from './DataGeneralTable.styles';

interface DataTableProps {
  rowFill?: 'odd' | 'even' | 'none';
  rows: ReactNode[][];
  columnsTemplate: string;
  firstColumnAsHeader?: boolean;
  firstRowAsHeader?: boolean;
}

const TABLE_CLICKABLE_GUARD_OPTIONS = {
  forceSelector: 'table[role="table"], table[role="table"] *',
};

const DataGeneralTable = ({
  rowFill = 'none',
  rows,
  columnsTemplate,
  firstColumnAsHeader = true,
  firstRowAsHeader = false,
}: DataTableProps) => {
  const tableRef = useNoopClickableGuard<HTMLTableElement>(
    TABLE_CLICKABLE_GUARD_OPTIONS,
  );
  const columnWidths = getColumnWidths(columnsTemplate, rows[0]?.length ?? 0);
  const columnDefinitions = getColumnDefinitions(columnWidths, rows[0] ?? []);
  const bodyRows = firstRowAsHeader ? rows.slice(1) : rows;
  const rowKeyRegistry = new Map<string, number>();

  const renderCellContent = (el: ReactNode) =>
    typeof el === 'string' ? <LuxTypography>{el}</LuxTypography> : el;

  const renderRow = (row: ReactNode[], isHeaderRow: boolean) => {
    const cellKeyRegistry = new Map<string, number>();

    return (
      <RowWrapper key={getRowKey(row, rowKeyRegistry)}>
        {row.map((el, cellIndex) => {
          const isHeaderCell =
            isHeaderRow ||
            (!firstRowAsHeader && firstColumnAsHeader && cellIndex === 0);
          const columnKey = columnDefinitions[cellIndex]?.key;
          const cellKey = getUniqueKey(
            `${columnKey || 'cell'}-${getCellKey(el)}`,
            cellKeyRegistry,
          );

          return isHeaderCell ? (
            <HeaderCellWrapper
              key={cellKey}
              scope={isHeaderRow ? 'col' : 'row'}
            >
              {renderCellContent(el)}
            </HeaderCellWrapper>
          ) : (
            <CellWrapper key={cellKey}>{renderCellContent(el)}</CellWrapper>
          );
        })}
      </RowWrapper>
    );
  };

  return (
    <TableWrapper ref={tableRef} rowFill={rowFill} role="table">
      {columnWidths.length > 0 && (
        <colgroup>
          {columnDefinitions.map(({ key, width }) => (
            <col key={key} style={{ width }} />
          ))}
        </colgroup>
      )}
      {firstRowAsHeader && rows[0] && (
        <TableHeaderWrapper>{renderRow(rows[0], true)}</TableHeaderWrapper>
      )}
      <TableBodyWrapper>
        {bodyRows.map(row => renderRow(row, false))}
      </TableBodyWrapper>
    </TableWrapper>
  );
};

const normalizeKeyPart = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/^-|-$/g, '') || 'empty';

const getTextFromNode = (node: ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(getTextFromNode).filter(Boolean).join('-');
  }

  if (isValidElement<{ children?: ReactNode }>(node)) {
    return getTextFromNode(node.props.children);
  }

  return '';
};

const getCellKey = (cell: ReactNode) => {
  if (typeof cell === 'string' || typeof cell === 'number') {
    return normalizeKeyPart(String(cell));
  }

  if (isValidElement(cell) && cell.key !== null) {
    return normalizeKeyPart(String(cell.key));
  }

  return normalizeKeyPart(getTextFromNode(cell));
};

const getUniqueKey = (baseKey: string, registry: Map<string, number>) => {
  const count = registry.get(baseKey) ?? 0;
  registry.set(baseKey, count + 1);

  return count === 0 ? baseKey : `${baseKey}-${count + 1}`;
};

const getRowKey = (row: ReactNode[], registry: Map<string, number>) =>
  getUniqueKey(`row-${row.map(getCellKey).join('-')}`, registry);

const getColumnDefinitions = (
  columnWidths: Array<string | undefined>,
  headerRow: ReactNode[],
) => {
  const columnKeyRegistry = new Map<string, number>();

  return columnWidths.map((width, columnIndex) => ({
    key: getUniqueKey(
      `column-${getCellKey(headerRow[columnIndex]) || normalizeKeyPart(width || '')}`,
      columnKeyRegistry,
    ),
    width,
  }));
};

const getColumnWidths = (columnsTemplate: string, columnCount: number) => {
  const repeatMatch = /^repeat\((\d+),\s*1fr\)$/.exec(columnsTemplate);
  if (repeatMatch) {
    return Array.from({ length: Number(repeatMatch[1]) }, () => undefined);
  }

  const parsedFrValues = columnsTemplate
    .split(/\s+/)
    .map(part => /^(\d+(?:\.\d+)?)fr$/.exec(part)?.[1])
    .map(value => (value ? Number(value) : undefined));

  if (!parsedFrValues.length || parsedFrValues.includes(undefined)) {
    return Array.from({ length: columnCount }, () => undefined);
  }

  const frValues = parsedFrValues as number[];
  const total = frValues.reduce((sum, value) => sum + (value ?? 0), 0);
  return frValues.map(value => `${((value / total) * 100).toFixed(2)}%`);
};

export default DataGeneralTable;
